<template>
  <v-dialog
    v-model="dialog"
    width="80%"
    max-width="1200px">
    <v-card>
      <div class="ipk">
        <div class="ipk-head">
          <div class="ipk-head__titles">
            <span class="ipk-title">
              <template v-if="type === 'FG'">Finished Goods Items</template>
              <template v-else-if="type === 'RM'">Raw Materials</template>
              <template v-else-if="type === 'BOM'">Assembly BOM</template>
            </span>
            <span
              v-if="!single"
              class="ipk-chip">
              {{ selected.length }} เลือกแล้ว
            </span>
          </div>
          <div class="ipk-head__search">
            <search-by-tags-and-brand
              v-if="type === 'FG'"
              v-model="query"
              append-icon="mdi-magnify"
              tags-width="300px"
              :is-mobile="isMobile"
              @on-search="onSearch()" />
            <search-box
              v-else
              v-model="query.search"
              :disabled="loading"
              @on-search="onSearch()" />
          </div>
        </div>

        <div
          class="ipk-body"
          :class="{ 'ipk-body--single': single }">
          <div class="ipk-browse">
            <product-table
              v-if="type === 'FG'"
              :items="items"
              :parent="parent"
              :loading="loading"
              :disabled="disabled"
              :is-selected="isSelected"
              @select="onSelect($event)"
              @preview="openPreview($event)" />
            <purchase-item-table
              v-else-if="type === 'RM'"
              :items="items"
              :loading="loading"
              :disabled="disabled"
              :is-selected="isSelected"
              @select="onSelect($event)"
              @preview="openPreview($event)" />
            <assembly-bom-table
              v-else-if="type === 'BOM'"
              :items="items"
              :loading="loading"
              :disabled="disabled"
              :is-selected="isSelected"
              @select="onSelect($event)"
              @preview="openPreview($event)" />
          </div>

          <div
            v-if="!single"
            class="ipk-cart">
            <div class="ipk-cart__label">
              เลือกแล้ว ({{ selected.length }})
            </div>
            <div
              v-if="!selected.length"
              class="ipk-cart__empty">
              ยังไม่ได้เลือกรายการ
            </div>
            <div
              v-for="(item, i) in selected"
              :key="`${i}-selected`"
              class="cartitem">
              <img
                :src="item.imageURL ? $convertImgix(`${item.imageURL}?w=128`) : noPicture"
                class="tthumb"
                :class="{ 'tthumb--clickable': item.imageURL }"
                alt="selected-image"
                @click="openPreview(item.imageURL)">
              <div class="cartitem__meta">
                <span class="cartitem__code">
                  {{ item.itemNo || item.lineItemNo }}
                </span>
                <span class="cartitem__line">
                  {{ type === 'RM' ? `Name: ${item.description || item.lineItemNo || '-'}` : `Color: ${item.color || '-'}` }}
                </span>
                <span class="cartitem__line">
                  {{ type === 'RM' ? `Description: ${item.description2 || item.lineDescription || '-'}` : `Size: ${item.size || '-'}` }}
                </span>
              </div>
              <v-btn
                icon
                small
                color="error"
                class="cartitem__del"
                :disabled="loading || disabled"
                @click="onRemove(item)">
                <v-icon size="18">
                  mdi-delete-outline
                </v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <div class="ipk-foot">
          <v-pagination
            v-model="query.page"
            color="secondary"
            :total-visible="7"
            :length="pageCount"
            :disabled="loading || disabled" />
        </div>

        <v-dialog
          v-model="previewDialog"
          max-width="640">
          <v-card class="ipk-preview">
            <div class="ipk-preview__head">
              <v-btn
                icon
                small
                @click="previewDialog = false">
                <v-icon size="18">
                  mdi-close
                </v-icon>
              </v-btn>
            </div>
            <img
              :src="previewURL ? $convertImgix(`${previewURL}?w=640`) : noPicture"
              class="ipk-preview__img"
              alt="preview">
          </v-card>
        </v-dialog>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'
import PurchaseItemsProvider from '@/resources/PurchaseItemsProvider'
import ProductProvider from '@/resources/ProductProvider'
import AssemblyBOMProvider from '@/resources/AssemblyBOMProvider'
import NoPicture from '@/assets/image/no_picture_available.png'
import SearchBox from '@/components/SearchBox.vue'
import SearchByTagsAndBrand from '@/components/SearchByTagsAndBrand.vue'
import PurchaseItemTable from './PurchaseItemTable.vue'
import ProductTable from './ProductTable.vue'
import AssemblyBomTable from './AssemblyBOMTable.vue'

const PurchaseItemsService = new PurchaseItemsProvider()
const ProductService = new ProductProvider()
const AssemblyBOMService = new AssemblyBOMProvider()

export default {
  components: {
    SearchBox,
    PurchaseItemTable,
    ProductTable,
    AssemblyBomTable,
    SearchByTagsAndBrand
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'RM'
    },
    parent: {
      type: Boolean,
      default: false
    },
    single: {
      type: Boolean,
      default: false
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      items: [],
      pageCount: 0,
      query: {
        search: '',
        page: 1,
        limit: 10
      },
      noPicture: NoPicture,
      previewDialog: false,
      previewURL: '',
      purchaseItemTypes: [
        {
          text: 'ผ้า',
          value: 'fabric'
        },
        {
          text: 'อะไหล่',
          value: 'material'
        },
        {
          text: 'อุปกรณ์แพ็ค',
          value: 'packaging'
        },
        {
          text: 'อุปกรณ์เสริม',
          value: 'supply'
        },
        {
          text: 'ค่าแรงตัดเย็บ',
          value: 'labour'
        },
        {
          text: 'ค่าขนส่ง',
          value: 'freight'
        }
      ]
    }
  },
  computed: {
    dialog: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    selected () {
      return this.selectedItems
    },
    isMobile () {
      return this.$vuetify.breakpoint.xs || this.$vuetify.breakpoint.sm
    }
  },
  watch: {
    type (val) {
      if (val === 'FG') {
        this.query = {
          page: 1,
          limit: 10,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          search: '',
          tags: [],
          tagOperation: 'OR',
          brand: 'all'
        }

        this.getProducts(true)
      } else if (this.type === 'RM') {
        this.getPurchaseItems(true)
      } else if (this.type === 'BOM') {
        this.getAssemblyBOMItems(true)
      }
    },
    'query.page': {
      handler () {
        if (this.type === 'FG') {
          this.getProducts()
        } else if (this.type === 'RM') {
          this.getPurchaseItems()
        } else if (this.type === 'BOM') {
          this.getAssemblyBOMItems()
        }
      },
      deep: true
    }
  },
  mounted () {
    if (this.type === 'FG') {
      this.query = {
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        search: '',
        tags: [],
        tagOperation: 'OR',
        brand: 'all'
      }

      this.getProducts(true)
    } else if (this.type === 'RM') {
      this.getPurchaseItems(true)
    } else if (this.type === 'BOM') {
      this.getAssemblyBOMItems(true)
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setLoading: 'Components/setLoading'
    }),
    async getAssemblyBOMItems (isClear = false) {
      try {
        this.loading = true

        if (isClear) {
          this.items = []
          this.query.page = 1
        }

        const { data } = await AssemblyBOMService.getItems({
          search: this.query.search,
          page: this.query.page,
          limit: this.query.limit
        })

        // this.items = data.results.map((re) => ({ ...re, itemType: 'BOM' }))
        this.items = data.results
        this.pageCount = data.pages
      } catch (error) {
        console.error('getAssemblyBOMItems', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getPurchaseItems (isClear = false) {
      try {
        this.loading = true

        if (isClear) {
          this.items = []
          this.query.page = 1
        }

        const { data } = await PurchaseItemsService.getItems({
          search: this.query.search,
          page: this.query.page,
          limit: this.query.limit
        })

        this.items = data.results.map((re) => ({ ...re, itemType: 'FG' }))
        this.pageCount = data.pages
      } catch (error) {
        console.error('getPurchaseItems', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    async getProducts (isClear = false) {
      try {
        this.loading = true

        if (isClear) {
          this.items = []
          this.query.page = 1
        }

        const { data } = await ProductService.getProducts(this.query)

        this.items = data.results.map((item) => ({
          ...item,
          imageURL: item.photoUrls[0],
          gwCollection: item.gwCollection?.nickname || '',
          itemType: 'FG',
          skus: item.skus.map((sku) => ({
              ...sku,
              name: item.name,
              model: item.model,
              colorName: sku.color.name,
              sizeName: sku.size.name,
              imageURL: item.photoUrls[0],
              groupCategories: item.groupCategories,
              categories: item.categories,
              gwCollection: item.gwCollection?.nickname || '',
              brand: item.brand,
              itemType: 'FG'
            }))
        }))
        this.pageCount = data.pages
      } catch (error) {
        console.error('getProducts', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    onSearch () {
      if (this.type === 'FG') {
        this.getProducts(true)
      } else if (this.type === 'RM') {
        this.getPurchaseItems(true)
      } else if (this.type === 'BOM') {
        this.getAssemblyBOMItems(true)
      }
    },
    isSelected (item) {
      return this.selected.some((s) => s.itemNo === item.itemNo)
        || this.selected.some((s) => s.itemNo === item.code)
        || (this.parent && this.selected.some((s) => s.itemNo === item.model))
    },
    onSelect (item) {
      if (this.selected.some((s) => s.itemNo === item.itemNo) || this.selected.some((s) => s.itemNo === item.code)) {
        return
      }

      if (this.parent && this.selected.some((s) => s.itemNo === item.model)) {
        return
      }

      this.$emit('select', item)
    },
    onRemove (item) {
      this.$emit('remove', item)
    },
    openPreview (url) {
      if (!url) {
        return
      }
      this.previewURL = url
      this.previewDialog = true
    },
    closeForm () {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.ipk {
  --ink: #18181b;
  --ink-soft: #3f3f46;
  --muted: #71717a;
  --muted-2: #a1a1aa;
  --line: #e4e4e7;
  --line-strong: #d4d4d8;
  --surface: #ffffff;
  --surface-2: #fafafa;
  --surface-3: #f4f4f5;
  --bg: #ececed;
  --r-sm: 6px;
  --r: 9px;
  --r-lg: 14px;
  background: var(--bg);
  padding: 16px;
  color: var(--ink);
}

/* Header */
.ipk-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 4px 4px 12px;
}
.ipk-head__titles {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ipk-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: var(--ink);
}
.ipk-chip {
  background: var(--surface-3);
  border: 1px solid var(--line);
  border-radius: 999px;
  padding: 1px 10px;
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-soft);
  white-space: nowrap;
}
.ipk-head__search {
  display: flex;
  justify-content: flex-end;
}

/* Body grid */
.ipk-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  grid-template-rows: minmax(0, 1fr);
  gap: 16px;
  align-items: stretch;
  height: min(70vh, 600px);
  min-height: 0;
}
.ipk-body--single {
  grid-template-columns: 1fr;
}
.ipk-browse {
  min-width: 0;
  min-height: 0;
  height: 100%;
}

/* Cart pane */
.ipk-cart {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  padding: 12px;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
}
.ipk-cart__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--muted);
  margin-bottom: 10px;
}
.ipk-cart__empty {
  text-align: center;
  color: var(--muted);
  font-size: 13px;
  padding: 24px 8px;
}
.cartitem {
  display: flex;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
  position: relative;
}
.cartitem:last-child {
  border-bottom: 0;
}
.cartitem .tthumb {
  width: 48px;
  height: 48px;
  border-radius: var(--r-sm);
  object-fit: cover;
  background: var(--surface-3);
  border: 1px solid var(--line);
  flex: none;
}
.cartitem__meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-right: 28px;
}
.cartitem__code {
  font-weight: 600;
  font-size: 13px;
  color: var(--ink);
}
.cartitem__line {
  font-size: 12px;
  color: var(--muted);
}
.cartitem__del {
  position: absolute;
  top: 6px;
  right: 0;
}

/* Footer */
.ipk-foot {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  margin-top: 14px;
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  background: var(--surface);
}

@media (max-width: 960px) {
  .ipk-body {
    grid-template-columns: 1fr;
    height: auto;
  }
  .ipk-browse {
    height: min(60vh, 480px);
  }
  .ipk-cart {
    height: auto;
    max-height: 320px;
  }
}
.tthumb--clickable {
  cursor: pointer;
}
.ipk-preview {
  overflow: hidden;
}
.ipk-preview__head {
  display: flex;
  justify-content: flex-end;
  padding: 8px;
}
.ipk-preview__img {
  display: block;
  width: 100%;
  max-height: 80vh;
  object-fit: contain;
}
</style>
